import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import AppHead from "../components/AppHead";
import PageWrap from "../components/PageWrap";
import Panel from "../components/Panel";
import ConfirmDialog from "../components/ConfirmDialog";

// ---------------------------------------------------------------------------
// Form error state
// ---------------------------------------------------------------------------

/**
 * Fehler werden als sprach-unabhängiges Objekt gehalten und erst beim Rendern
 * aufgelöst (Req 12.6):
 * - lokale Meldungen → `{ kind: "key" }` mit einem Katalog-Schlüssel
 * - API-Fehler → `{ kind: "api" }` mit dem Fehler-OBJEKT (via errorMessage)
 */
type DisplayError =
  | { kind: "key"; key: string }
  | { kind: "api"; err: unknown };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Die vier Aufbewahrungswerte für alte Wochenpläne (kein "nie löschen"). */
type RetentionValue = "immediate" | "1w" | "1m" | "3m";
const RETENTION_VALUES: readonly RetentionValue[] = [
  "immediate",
  "1w",
  "1m",
  "3m",
];

/** Fortbewegungsmittel einer Fahrt (mirrors backend TransportMode). */
type TransportMode = "bike" | "car" | "bus";

/** Die drei Transportmittel in fester Reihenfolge (für die ToggleButtons). */
const TRANSPORT_MODES: readonly TransportMode[] = ["bike", "car", "bus"];

/** Die beiden Fahrtrichtungen einer Standard-Fahrtdetail-Eingabe. */
type Direction = "morning" | "afternoon";
const DIRECTIONS: readonly Direction[] = ["morning", "afternoon"];

/** Wie die Standardwerte auf zukünftige Tage angewendet werden (Req 3.1/3.2). */
type SaveMode = "overwrite" | "fill-empty";

/** Request-Body für PUT /api/groups/:groupId/defaults/:direction (Req 3.2). */
interface UpdateGroupDefaultBody {
  startTime: string | null;
  transportMode: TransportMode;
  meetingPoint: string | null;
  saveMode: SaveMode;
}

/**
 * Standard-Fahrtdetails einer Gruppe pro Richtung (mirrors backend
 * TripDefaults in backend/src/schemas/entities.ts, migration 011).
 */
interface TripDefaults {
  startTime: string | null;
  transportMode: TransportMode;
  meetingPoint: string | null;
}

interface Group {
  id: string;
  name: string;
  invitationCode: string;
  createdBy: string;
  createdAt?: string;
  retention: RetentionValue;
  notifyOnChangeEnabled: boolean;
  morningDefault: TripDefaults;
  afternoonDefault: TripDefaults;
}

interface GroupMember {
  groupId: string;
  userId: string;
  name: string;
  email: string;
  role: "admin" | "member";
  isActive: boolean;
  joinedAt: string;
}

interface GroupDetailsResponse {
  group: Group;
  members: GroupMember[];
}

interface RegenerateResponse {
  invitationCode: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Löst ein DisplayError-Objekt in der Aktive_Sprache auf (Req 12.6). */
function resolveError(
  t: (key: string, p?: Record<string, string | number>) => string,
  error: DisplayError,
): string {
  return error.kind === "key" ? t(error.key) : errorMessage(t, error.err);
}

/**
 * Lokaler, editierbarer Formularzustand einer Richtung. NULL-Werte aus dem
 * Server werden für die MUI-Textfelder auf "" normalisiert.
 */
interface DefaultForm {
  startTime: string; // "" wenn nicht gesetzt (null)
  transportMode: TransportMode;
  meetingPoint: string; // "" wenn nicht gesetzt (null)
}

/** Seed-Helfer: TripDefaults (API) → DefaultForm (lokaler State). */
function toDefaultForm(def: TripDefaults): DefaultForm {
  return {
    startTime: def.startTime ?? "",
    transportMode: def.transportMode,
    meetingPoint: def.meetingPoint ?? "",
  };
}

// ---------------------------------------------------------------------------
// GroupSettings page
// ---------------------------------------------------------------------------

export default function GroupSettings() {
  const { id } = useParams<{ id: string }>();
  const groupId = id ?? "";
  const { user } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    error: fetchError,
  } = useQuery<GroupDetailsResponse>({
    queryKey: ["group", groupId],
    queryFn: () => apiClient.get<GroupDetailsResponse>(`/api/groups/${groupId}`),
    enabled: groupId !== "",
  });

  // Determine whether the logged-in user is an admin of this group.
  const members = data?.members ?? [];
  const isAdmin =
    user != null &&
    members.some((m) => m.userId === user.id && m.role === "admin");

  // Copy the invitation code to the clipboard with brief "Copied!" feedback.
  const [copied, setCopied] = useState(false);
  const invitationCode = data?.group.invitationCode ?? "";

  async function copyInvitationCode() {
    try {
      await navigator.clipboard.writeText(invitationCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context).
      setCopied(false);
    }
  }

  // Regenerate invitation code (admin only).
  const regenerate = useMutation({
    mutationFn: () =>
      apiClient.post<RegenerateResponse>(`/api/groups/${groupId}/invite`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
  });

  // Remove a member (admin only).
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const removeMember = useMutation({
    mutationFn: (targetUserId: string) =>
      apiClient.delete<void>(`/api/groups/${groupId}/members/${targetUserId}`),
    onMutate: (targetUserId: string) => {
      setRemovingUserId(targetUserId);
    },
    onSettled: () => {
      setRemovingUserId(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
  });

  // Delete the entire group (admin only). Cascades on the server.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const deleteGroup = useMutation({
    mutationFn: () => apiClient.delete<void>(`/api/groups/${groupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setConfirmDeleteOpen(false);
      navigate("/groups");
    },
  });

  // Update the per-group retention setting (admin only).
  const currentRetention = data?.group.retention ?? "3m";
  const updateRetention = useMutation({
    mutationFn: (retention: RetentionValue) =>
      apiClient.put<{ retention: RetentionValue }>(
        `/api/groups/${groupId}/retention`,
        { retention },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
  });

  function handleRetentionChange(
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    // RadioGroup always provides one of the four values; only mutate on change.
    if (value !== currentRetention) {
      updateRetention.mutate(value as RetentionValue);
    }
  }

  // Update the per-group notify-on-change setting (admin only).
  const notifyOnChangeEnabled = data?.group.notifyOnChangeEnabled ?? false;
  const updateNotifyOnChange = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.put<{ notifyOnChangeEnabled: boolean }>(
        `/api/groups/${groupId}/notify-on-change`,
        { enabled },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
  });

  // ---------------------------------------------------------------------------
  // Standard-Fahrtdetails (Group_Defaults) — member-editierbar (kein Admin-Gate).
  // Lokaler Formularzustand pro Richtung, geseedet aus den Server-Defaults
  // (Req 6.1). Re-seed, sobald neue Gruppendaten geladen werden.
  // ---------------------------------------------------------------------------
  const [defaultForms, setDefaultForms] = useState<
    Record<Direction, DefaultForm>
  >({
    morning: toDefaultForm(
      data?.group.morningDefault ?? {
        startTime: null,
        transportMode: "bike",
        meetingPoint: null,
      },
    ),
    afternoon: toDefaultForm(
      data?.group.afternoonDefault ?? {
        startTime: null,
        transportMode: "bike",
        meetingPoint: null,
      },
    ),
  });

  useEffect(() => {
    if (data?.group != null) {
      setDefaultForms({
        morning: toDefaultForm(data.group.morningDefault),
        afternoon: toDefaultForm(data.group.afternoonDefault),
      });
    }
  }, [data?.group]);

  // Partielles Update einer Richtung im lokalen Formularzustand.
  function updateDefaultForm(direction: Direction, patch: Partial<DefaultForm>) {
    setDefaultForms((prev) => ({
      ...prev,
      [direction]: { ...prev[direction], ...patch },
    }));
  }

  // Seiten-weite Snackbar (Erfolg/Fehler), gespiegelt nach WeeklyPlanView.
  const [snack, setSnack] = useState<
    { variant: "success" | "error"; message: string } | null
  >(null);

  // Welche Richtung hat gerade den Save_Mode-Dialog offen (null = geschlossen).
  const [saveDialogDirection, setSaveDialogDirection] =
    useState<Direction | null>(null);

  // Speichern-Mutation: persistiert eine Richtung + propagiert sie je nach
  // Save_Mode. Bei Erfolg Server-Daten neu laden (re-seedet die Anzeige über
  // den useEffect, Req 6.3); bei Fehler lokalen Zustand NICHT zurücksetzen
  // (vorherige Werte bleiben erhalten, Req 6.4).
  const saveDefault = useMutation({
    mutationFn: (vars: { direction: Direction; body: UpdateGroupDefaultBody }) =>
      apiClient.put<{ default: TripDefaults }>(
        `/api/groups/${groupId}/defaults/${vars.direction}`,
        vars.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
      setSnack({
        variant: "success",
        message: t("groupSettings.defaults.saved"),
      });
    },
    onError: () => {
      setSnack({
        variant: "error",
        message: t("groupSettings.defaults.saveError"),
      });
    },
  });

  // Baut den Request-Body aus dem lokalen Formular der Richtung und löst mit
  // dem gewählten Save_Mode die Mutation aus. Leere/Whitespace-Werte → null,
  // damit geleerte Felder als null gesendet werden (Req 2.6/4.1/4.2).
  function confirmSave(direction: Direction, saveMode: SaveMode) {
    const form = defaultForms[direction];
    const startTime = form.startTime.trim() === "" ? null : form.startTime;
    const meetingPoint =
      form.meetingPoint.trim() === "" ? null : form.meetingPoint.trim();
    saveDefault.mutate({
      direction,
      body: {
        startTime,
        transportMode: form.transportMode,
        meetingPoint,
        saveMode,
      },
    });
    setSaveDialogDirection(null);
  }

  // Rendert eine Richtungs-Teilsektion (Hinweg/Rückweg) des Defaults-Panels.
  function renderDirectionSection(direction: Direction) {
    const form = defaultForms[direction];
    const directionLabel = t(`groupSettings.defaults.${direction}.title`);
    return (
      <Stack key={direction} gap={1.5}>
        <Typography fontWeight={600}>{directionLabel}</Typography>

        {/* Startzeit */}
        <TextField
          label={t("groupSettings.defaults.startTime")}
          type="time"
          value={form.startTime}
          onChange={(e) =>
            updateDefaultForm(direction, { startTime: e.target.value })
          }
          inputProps={{ step: 60 }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        {/* Transportmittel */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={form.transportMode}
          aria-label={t("groupSettings.defaults.transport.aria", {
            direction: directionLabel,
          })}
          onChange={(_e, v: TransportMode | null) => {
            if (v) updateDefaultForm(direction, { transportMode: v });
          }}
        >
          {TRANSPORT_MODES.map((m) => (
            <ToggleButton key={m} value={m} sx={{ minHeight: 48 }}>
              {t(`weeklyPlan.transport.${m}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Treffpunkt */}
        <TextField
          label={t("groupSettings.defaults.meetingPoint")}
          value={form.meetingPoint}
          onChange={(e) =>
            updateDefaultForm(direction, { meetingPoint: e.target.value })
          }
          inputProps={{ maxLength: 255 }}
          fullWidth
        />

        {/* Speichern pro Richtung — öffnet den Save_Mode-Dialog, bevor eine
            Anfrage gesendet wird (Req 3.1). */}
        <Button
          variant="contained"
          onClick={() => setSaveDialogDirection(direction)}
          sx={{ minHeight: 44, alignSelf: "flex-start" }}
        >
          {t("groupSettings.defaults.save")}
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <AppHead
        title={
          data?.group.name
            ? t("groupSettings.titleNamed", { name: data.group.name })
            : t("groupSettings.title")
        }
        backTo={`/groups/${groupId}/plan`}
        backAriaLabel={t("groupSettings.back")}
      />

      <PageWrap>
        {/* Loading state */}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }} aria-live="polite">
            <CircularProgress aria-label={t("groupSettings.loading")} />
          </Stack>
        )}

        {/* Fetch error */}
        {!isLoading && fetchError != null && (
          <Alert severity="error" role="alert">
            {errorMessage(t, fetchError)}
          </Alert>
        )}

        {/* Loaded content */}
        {!isLoading && fetchError == null && data != null && (
          <>
            {/* Invitation code */}
            <Panel>
              <Typography fontWeight={700} mb={1.5}>
                {t("groupSettings.invite.title")}
              </Typography>
              <Box
                data-testid="invitation-code"
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  py: 2,
                  textAlign: "center",
                  fontFamily: "monospace",
                  fontSize: { xs: 28, sm: 34 },
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  wordBreak: "break-all",
                }}
              >
                {data.group.invitationCode}
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={copyInvitationCode}
                  aria-label={t("groupSettings.invite.copyAria")}
                  sx={{ minHeight: 44 }}
                >
                  {copied
                    ? t("groupSettings.invite.copied")
                    : t("groupSettings.invite.copy")}
                </Button>
                {isAdmin && (
                  <Button
                    variant="contained"
                    onClick={() => regenerate.mutate()}
                    disabled={regenerate.isPending}
                    aria-busy={regenerate.isPending}
                    sx={{ minHeight: 44 }}
                  >
                    {regenerate.isPending
                      ? t("groupSettings.invite.regenerating")
                      : t("groupSettings.invite.regenerate")}
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" mt={1.5}>
                {t("groupSettings.invite.hint")}
                {isAdmin ? t("groupSettings.invite.hintAdmin") : ""}
              </Typography>
              {isAdmin && regenerate.isError && (
                <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                  {resolveError(t, { kind: "api", err: regenerate.error })}
                </Alert>
              )}
            </Panel>

            {/* Member list */}
            <Panel>
              <Typography fontWeight={700} mb={1}>
                {t("groupSettings.members.title", { count: members.length })}
              </Typography>
              <Stack divider={<Divider />}>
                {members.map((member) => {
                    const isSelf = user != null && member.userId === user.id;
                    const canRemove = isAdmin && !isSelf;
                    const isRemoving =
                      removeMember.isPending && removingUserId === member.userId;
                    return (
                      <Stack
                        key={member.userId}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        flexWrap="wrap"
                        py={1.25}
                      >
                        <Box minWidth={0}>
                          <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                            {member.name}
                            {isSelf ? t("groupSettings.members.you") : ""}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ wordBreak: "break-word" }}
                          >
                            {member.email}
                          </Typography>
                          <Chip
                            size="small"
                            color={member.role === "admin" ? "primary" : "default"}
                            variant={member.role === "admin" ? "filled" : "outlined"}
                            label={
                              member.role === "admin"
                                ? t("groupSettings.members.role.admin")
                                : t("groupSettings.members.role.member")
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        {canRemove && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => removeMember.mutate(member.userId)}
                            disabled={removeMember.isPending}
                            aria-busy={isRemoving}
                            aria-label={t("groupSettings.members.removeAria", {
                              name: member.name,
                            })}
                            sx={{ minHeight: 44 }}
                          >
                            {isRemoving
                              ? t("groupSettings.members.removing")
                              : t("groupSettings.members.remove")}
                          </Button>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              {isAdmin && removeMember.isError && (
                <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                  {resolveError(t, { kind: "api", err: removeMember.error })}
                </Alert>
              )}
            </Panel>

            {/* Default trip details (member-editable, NO admin gate) */}
            <Panel>
              <Typography fontWeight={700} mb={0.5}>
                {t("groupSettings.defaults.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t("groupSettings.defaults.hint")}
              </Typography>
              <Stack divider={<Divider />} gap={2.5}>
                {DIRECTIONS.map((direction) =>
                  renderDirectionSection(direction),
                )}
              </Stack>
            </Panel>

            {/* Retention of old weekly plans (read-only for members) */}
            <Panel>
              <FormControl
                component="fieldset"
                disabled={updateRetention.isPending || !isAdmin}
                sx={{ width: "100%" }}
              >
                <FormLabel
                  component="legend"
                  id="retention-label"
                  sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
                >
                  {t("groupSettings.retention.title")}
                </FormLabel>
                <RadioGroup
                  row
                  name="retention"
                  aria-labelledby="retention-label"
                  value={currentRetention}
                  onChange={handleRetentionChange}
                  sx={{ gap: 1 }}
                >
                  {RETENTION_VALUES.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={t(`groupSettings.retention.${value}`)}
                      sx={{ minHeight: 44 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              {!isAdmin && (
                <Typography variant="body2" color="text.secondary" mt={1.5}>
                  {t("groupSettings.retention.adminOnlyHint")}
                </Typography>
              )}
              {isAdmin && updateRetention.isError && (
                <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                  {resolveError(t, { kind: "api", err: updateRetention.error })}
                </Alert>
              )}
            </Panel>

            {/* Notify-on-change prompt setting (read-only for members) */}
            <Panel>
              <Typography fontWeight={700} mb={1.5}>
                {t("groupSettings.notifyOnChange.title")}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifyOnChangeEnabled}
                    onChange={(e) =>
                      updateNotifyOnChange.mutate(e.target.checked)
                    }
                    disabled={updateNotifyOnChange.isPending || !isAdmin}
                  />
                }
                label={t("groupSettings.notifyOnChange.label")}
                sx={{ minHeight: 44 }}
              />
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {t("groupSettings.notifyOnChange.hint")}
              </Typography>
              {!isAdmin && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {t("groupSettings.notifyOnChange.adminOnlyHint")}
                </Typography>
              )}
              {isAdmin && updateNotifyOnChange.isError && (
                <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                  {resolveError(t, {
                    kind: "api",
                    err: updateNotifyOnChange.error,
                  })}
                </Alert>
              )}
            </Panel>

            {/* Delete whole group (admin only) */}
            {isAdmin && (
              <Panel>
                <Typography fontWeight={700} mb={1.5}>
                  {t("groupSettings.delete.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t("groupSettings.delete.warning")}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={deleteGroup.isPending}
                  aria-busy={deleteGroup.isPending}
                  sx={{ minHeight: 44 }}
                >
                  {t("groupSettings.delete.button")}
                </Button>
                {deleteGroup.isError && (
                  <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                    {resolveError(t, { kind: "api", err: deleteGroup.error })}
                  </Alert>
                )}
                <ConfirmDialog
                  open={confirmDeleteOpen}
                  title={t("groupSettings.delete.title")}
                  message={t("groupSettings.delete.warning")}
                  pending={deleteGroup.isPending}
                  onCancel={() => setConfirmDeleteOpen(false)}
                  onConfirm={() => deleteGroup.mutate()}
                />
              </Panel>
            )}
          </>
        )}

        {/* Save_Mode-Dialog: Wahl zwischen overwrite / fill-empty / abbrechen,
            bevor gespeichert wird (Req 3.1/3.2). */}
        <Dialog
          open={saveDialogDirection != null}
          onClose={() => setSaveDialogDirection(null)}
          aria-labelledby="save-default-dialog-title"
        >
          <DialogTitle id="save-default-dialog-title">
            {t("groupSettings.defaults.saveDialog.title")}
          </DialogTitle>
          <DialogContent>
            <Stack gap={1.5} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={() => {
                  if (saveDialogDirection != null)
                    confirmSave(saveDialogDirection, "overwrite");
                }}
                sx={{ minHeight: 44 }}
              >
                {t("groupSettings.defaults.saveDialog.overwrite")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (saveDialogDirection != null)
                    confirmSave(saveDialogDirection, "fill-empty");
                }}
                sx={{ minHeight: 44 }}
              >
                {t("groupSettings.defaults.saveDialog.fillEmpty")}
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogDirection(null)}>
              {t("groupSettings.defaults.saveDialog.cancel")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar (Erfolg/Fehler beim Speichern der Standardwerte). */}
        <Snackbar
          open={snack != null}
          autoHideDuration={2200}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {snack != null ? (
            <Alert
              severity={snack.variant}
              variant="filled"
              onClose={() => setSnack(null)}
              sx={{ width: "100%" }}
            >
              {snack.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </PageWrap>
    </>
  );
}
